import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { EnvironmentService } from '@indutrust/shared-env';
import { filter, first, map } from 'rxjs/operators';
import { AuthSessionStore } from '../+state';
import { navigateOrReport } from '../util/navigate-or-report.util';

/**
 * Route guard for the factory **editor shell** (`/factory/:id/edit`).
 *
 * Allows:
 * - `supplier` — own-factory lifecycle (production)
 * - `admin` — cross-company support / verification
 * - `government` — **demo-only** exception when the factory domain is
 *   MSW-mocked ({@link EnvironmentService.isDomainMocked}(`'factory'`)),
 *   so mock-demo presenters can open and mutate any factory from the
 *   public profile path without changing the production product model
 *   (government remains observatory / read-only outside a mocked factory
 *   domain). Hybrid demos that leave `factory` on the live API do **not**
 *   get this exception — that would open staging editors for government.
 *
 * Does **not** replace {@link supplierGuard} on `/factories` or
 * `/virtual-tour` — those stay supplier/admin only.
 *
 * Unauthenticated requests are expected to be handled upstream by
 * {@link authGuard}. Pair with {@link entityEditableGuard} for per-entity
 * `can_edit` checks.
 *
 * @returns An {@link import('rxjs').Observable} resolving to `true` when
 *   the session may open the editor, `false` otherwise (redirect to `/`).
 * @example
 * import { Routes } from '@angular/router';
 * import {
 *   entityEditableGuard,
 *   factoryEditorGuard,
 * } from '@indutrust/auth-data';
 *
 * export const routes: Routes = [
 *   {
 *     path: ':id/edit',
 *     canActivate: [
 *       factoryEditorGuard,
 *       entityEditableGuard(factoryWritabilityResolver),
 *     ],
 *     loadComponent: async () =>
 *       import('@indutrust/factory-feature').then((m) => m.FactoryEditComponent),
 *   },
 * ];
 */
export const factoryEditorGuard: CanActivateFn = () => {
  const authStore = inject(AuthSessionStore);
  const environment = inject(EnvironmentService);
  const router = inject(Router);

  return toObservable(authStore.authenticationInitialized).pipe(
    filter((initialized): boolean => initialized),
    first(),
    map(() => {
      const demoGovernment =
        authStore.isGovernment() && environment.isDomainMocked('factory');
      if (authStore.isSupplier() || authStore.isAdmin() || demoGovernment) {
        return true;
      }
      navigateOrReport(router, ['/'], 'Navigation to home failed');
      return false;
    })
  );
};
